import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { IconButton } from './Icon';

export interface FormField {
  key: string;
  label: string;
  type?: 'text' | 'number';
  value: string;
  step?: string;
  min?: number;
  placeholder?: string;
}

interface PromptRequest {
  kind: 'prompt';
  message: string;
  defaultValue: string;
  resolve: (value: string | null) => void;
}

interface ConfirmRequest {
  kind: 'confirm';
  message: string;
  danger: boolean;
  resolve: (value: boolean) => void;
}

interface FormRequest {
  kind: 'form';
  message: string;
  fields: FormField[];
  resolve: (value: Record<string, string> | null) => void;
}

type DialogRequest = PromptRequest | ConfirmRequest | FormRequest;

export interface Dialogs {
  /** Resolves with the entered text, or null when cancelled. */
  prompt: (message: string, defaultValue?: string) => Promise<string | null>;
  confirm: (message: string, options?: { danger?: boolean }) => Promise<boolean>;
  /** Multi-field form. Resolves with the field values keyed by `key`, or null when cancelled. */
  form: (title: string, fields: FormField[]) => Promise<Record<string, string> | null>;
}

const DialogContext = createContext<Dialogs | null>(null);

export function useDialogs(): Dialogs {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useDialogs must be used inside <DialogProvider>');
  return ctx;
}

export function DialogProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<DialogRequest | null>(null);
  const [value, setValue] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const firstInputRef = useRef<HTMLInputElement | null>(null);

  const dialogs = useMemo<Dialogs>(
    () => ({
      prompt: (message, defaultValue = '') =>
        new Promise<string | null>((resolve) => {
          setValue(defaultValue);
          setRequest({ kind: 'prompt', message, defaultValue, resolve });
        }),
      confirm: (message, options) =>
        new Promise<boolean>((resolve) => {
          setRequest({ kind: 'confirm', message, danger: options?.danger ?? true, resolve });
        }),
      form: (title, fields) =>
        new Promise<Record<string, string> | null>((resolve) => {
          setValues(Object.fromEntries(fields.map((f) => [f.key, f.value])));
          setRequest({ kind: 'form', message: title, fields, resolve });
        }),
    }),
    [],
  );

  const close = useCallback(
    (accepted: boolean) => {
      setRequest((current) => {
        if (!current) return null;
        if (current.kind === 'prompt') current.resolve(accepted ? value : null);
        else if (current.kind === 'form') current.resolve(accepted ? values : null);
        else current.resolve(accepted);
        return null;
      });
    },
    [value, values],
  );

  // Focus the first field when a dialog opens. `request` changes identity exactly once per open,
  // so this must NOT depend on `close` - that is rebuilt on every keystroke and would re-select
  // the field while the user is still typing.
  useEffect(() => {
    if (!request) return;
    firstInputRef.current?.focus();
    firstInputRef.current?.select();
  }, [request]);

  useEffect(() => {
    if (!request) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [request, close]);

  return (
    <DialogContext.Provider value={dialogs}>
      {children}
      {request ? (
        <div className="modal-backdrop" role="presentation" onClick={() => close(false)}>
          <form
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-label={request.message}
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => {
              e.preventDefault();
              close(true);
            }}
          >
            <p className="modal__message">{request.message}</p>
            {request.kind === 'prompt' ? (
              <input
                ref={firstInputRef}
                type="text"
                className="modal__input"
                value={value}
                aria-label={request.message}
                onChange={(e) => setValue(e.target.value)}
              />
            ) : null}
            {request.kind === 'form' ? (
              <div className="modal__fields">
                {request.fields.map((field, index) => (
                  <label key={field.key}>
                    {field.label}
                    <input
                      ref={index === 0 ? firstInputRef : undefined}
                      type={field.type ?? 'text'}
                      step={field.step}
                      min={field.min}
                      placeholder={field.placeholder}
                      value={values[field.key] ?? ''}
                      onChange={(e) => setValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    />
                  </label>
                ))}
              </div>
            ) : null}
            <div className="modal__actions">
              <IconButton icon="close" label="Cancel" onClick={() => close(false)} />
              <IconButton
                icon="check"
                label="Confirm"
                type="submit"
                primary={request.kind !== 'confirm' || !request.danger}
                danger={request.kind === 'confirm' && request.danger}
              />
            </div>
          </form>
        </div>
      ) : null}
    </DialogContext.Provider>
  );
}
